import { ExpressedOutcomeEnum, SideEnum, toBig, toBigWithDefault } from '@talos/kyoko';
import Big from 'big.js';

export type PredictionOutcomeQuotes = {
  yesAsk?: string;
  yesBid?: string;
};

export function getYesMid(yesBid: number, yesAsk: number): number {
  return (yesBid + yesAsk) / 2;
}

/** Unrounded chance % from the Yes book mid (e.g. 0.42/0.44 → 43). Round only at display. */
export function getMidChancePct(yesBid: number, yesAsk: number): number {
  return getYesMid(yesBid, yesAsk) * 100;
}

/**
 * Unrounded chance % from a last traded Yes price (e.g. '0.425' → 42.5). Round only at display.
 * A non-positive or absent price means the outcome has not traded — those return undefined.
 */
export function getChancePct(lastTradedPrice: string | undefined): number | undefined {
  const price = toBig(lastTradedPrice);
  if (price == null || price.lte(0)) {
    return undefined;
  }
  return price.times(100).toNumber();
}

function complementProbability(price: string): string {
  return Big(1).minus(price).toFixed();
}

/**
 * Max payout if the position wins. Each contract settles at 1 unit of the counter currency,
 * so payout = number of contracts = cost / price.
 */
export function getPredictionPayout(
  counterAmount: string | number | null | undefined,
  outcomeDisplayPrice: string | undefined
): string | undefined {
  if (counterAmount == null || counterAmount === '' || outcomeDisplayPrice == null || outcomeDisplayPrice === '') {
    return undefined;
  }
  try {
    const price = Big(outcomeDisplayPrice);
    if (price.lte(0)) {
      return undefined;
    }
    return Big(counterAmount).div(price).toFixed();
  } catch {
    return undefined;
  }
}

/**
 * Max payout from order-form inputs. Contract quantity is already the payout; counter-currency
 * quantity is spend notional divided by the outcome display price.
 */
export function getPredictionOrderPayout(
  quantity: string | undefined,
  outcomeDisplayPrice: string | undefined,
  isContractsQuantity: boolean
): string | undefined {
  if (quantity == null || quantity === '' || quantity === '-') {
    return undefined;
  }
  if (isContractsQuantity) {
    return quantity;
  }
  return getPredictionPayout(quantity, outcomeDisplayPrice);
}

/**
 * Converts an outcome-display price (what the form shows for Yes/No) to the yes-book price.
 * The No outcome is the complement of its display price.
 */
export function outcomeDisplayPriceToYesBookPrice(
  outcome: ExpressedOutcomeEnum,
  outcomeDisplayPrice: string | undefined
): string | undefined {
  if (outcomeDisplayPrice == null || outcomeDisplayPrice === '') {
    return undefined;
  }
  if (!Number.isFinite(Number(outcomeDisplayPrice))) {
    return undefined;
  }
  return outcome === ExpressedOutcomeEnum.No ? complementProbability(outcomeDisplayPrice) : outcomeDisplayPrice;
}

/** Converts a yes-book OMS price back to the selected outcome price shown in the form. */
export function yesBookPriceToOutcomeDisplay(outcome: ExpressedOutcomeEnum, yesBookPrice: string): string {
  if (outcome === ExpressedOutcomeEnum.Yes) {
    return yesBookPrice;
  }
  try {
    return complementProbability(yesBookPrice);
  } catch {
    return yesBookPrice;
  }
}

/**
 * Mid-price for a Yes/No outcome, used for complementary display on OMS outcome toggles.
 * Yes-mid and No-mid always sum to 1 (100%). Never use this to prime an order price, and do not
 * use it for prediction-card Yes/No buttons (those show the executable buy side).
 */
export function getPredictionOutcomeMidPrice(
  outcome: ExpressedOutcomeEnum,
  { yesAsk, yesBid }: PredictionOutcomeQuotes
): string | undefined {
  if (!yesAsk || !yesBid) {
    return undefined;
  }
  const yesMid = getYesMid(Number(yesBid), Number(yesAsk));
  if (!Number.isFinite(yesMid)) {
    return undefined;
  }
  return outcome === ExpressedOutcomeEnum.Yes ? String(yesMid) : String(1 - yesMid);
}

/**
 * Outcome-display price to prime a principal prediction order with, using the executable side.
 * Buy Yes → Yes ask; Buy No → No ask (1 - Yes bid).
 * Sell Yes → Yes bid; Sell No → No bid (1 - Yes ask).
 * Returns an exact, increment-aligned price (no floating-point drift) so the OMS price field validates.
 */
export function getPredictionOutcomePrice(
  outcome: ExpressedOutcomeEnum,
  side: SideEnum,
  { yesAsk, yesBid }: PredictionOutcomeQuotes
): string | undefined {
  const usesYesAsk =
    (outcome === ExpressedOutcomeEnum.Yes && side === SideEnum.Buy) ||
    (outcome === ExpressedOutcomeEnum.No && side === SideEnum.Sell);
  const yesPrice = usesYesAsk ? yesAsk : yesBid;
  if (yesPrice == null || yesPrice === '' || !Number.isFinite(Number(yesPrice))) {
    return undefined;
  }
  return outcome === ExpressedOutcomeEnum.Yes ? yesPrice : complementProbability(yesPrice);
}

/**
 * Maps selected outcome + buy/sell intent to the YES-book OMS side, without requiring prices.
 * Buy No → Sell Yes; Sell No → Buy Yes.
 */
export function toYesBookSide(outcome: ExpressedOutcomeEnum, isBuy: boolean): SideEnum {
  if (outcome === ExpressedOutcomeEnum.Yes) {
    return isBuy ? SideEnum.Buy : SideEnum.Sell;
  }
  return isBuy ? SideEnum.Sell : SideEnum.Buy;
}

type OrderSubmissionWithSideAndPrice = {
  Side?: SideEnum;
  Price?: string | null;
};

/** Converts SalesOrder prediction intent into yes-book Side/Price on an outgoing request. */
export function applyPredictionYesBookToSubmission(
  request: OrderSubmissionWithSideAndPrice,
  outcome: ExpressedOutcomeEnum,
  isBuy: boolean
): void {
  if (request.Price != null && request.Price !== '') {
    request.Price = outcomeDisplayPriceToYesBookPrice(outcome, request.Price) ?? request.Price;
  }

  if ('Side' in request) {
    request.Side = toYesBookSide(outcome, isBuy);
  }
}

/**
 * Prediction market rows stay Yes-normalized even when the user expressed the No outcome, while the
 * order itself is reported in the expressed outcome's terms. Each contract pays out one unit of the
 * quote currency, so the No side of a fill cost whatever the Yes side did not.
 */
export function getExpressedOutcomeCumAmt(
  market: { CumQty?: string; CumAmt?: string },
  expressedOutcome: ExpressedOutcomeEnum | undefined
): Big {
  const cumAmt = toBigWithDefault(market.CumAmt, 0);

  if (expressedOutcome !== ExpressedOutcomeEnum.No) {
    return cumAmt;
  }

  return toBigWithDefault(market.CumQty, 0).minus(cumAmt);
}
